/**
 * LocalizedText: a user-facing label that may carry one value per locale (plan §7.3).
 *
 *   type LocalizedText = string | { [locale: string]: string };
 *
 * A plain string means "the same text in every locale" and stays valid everywhere a label was a
 * string before. A map holds one value per locale code (`en`, `de`, `de-AT`, `pt-BR`, …).
 * Resolution (`resolveText`) tries the exact locale, then its language (`de-AT` → `de`), then the
 * fallback (`en` by default), then the first non-empty value. Empty values count as missing, so
 * an empty translation never hides the fallback.
 *
 * Owner: platform-contracts (type, schema, resolution). Server-side resolution for the member's
 * language and the translation UI belong to i18n-metadata and data-model-objects.
 */
import { z } from 'zod';

/** Locale codes: a 2–3 letter language, optional 4-letter script, optional region (BCP 47 subset). */
export const LOCALE_CODE_RE = /^[a-z]{2,3}(?:-[A-Z][a-z]{3})?(?:-(?:[A-Z]{2}|\d{3}))?$/;

/** Limits of a LocalizedText value (schema defaults; callers may tighten `maxLength`). */
export const LOCALIZED_TEXT_LIMITS = {
  /** Locales per map. */
  maxLocales: 12,
  /** Characters per value (labels and descriptions; long-form text uses documents, not labels). */
  maxLength: 2000,
} as const;

export type LocalizedTextMap = { [locale: string]: string };
export type LocalizedText = string | LocalizedTextMap;

export const LocaleCode = z
  .string()
  .regex(LOCALE_CODE_RE, { message: 'Use a locale code such as "en", "de" or "de-AT"' });
export type LocaleCode = z.infer<typeof LocaleCode>;

export type LocalizedTextOptions = {
  /** Maximum characters per value (default 2000). */
  maxLength?: number;
  /** Minimum characters per value (default 0 for a plain string, 1 for map values). */
  minLength?: number;
};

/**
 * Zod schema of a LocalizedText. Map values must not be empty (a missing translation is simply
 * left out); keys must be locale codes; at most 12 locales; every value obeys `maxLength`.
 */
export function localizedText(options: LocalizedTextOptions = {}) {
  const maxLength = options.maxLength ?? LOCALIZED_TEXT_LIMITS.maxLength;
  const minLength = options.minLength ?? 0;
  const value = (min: number) => z.string().min(min).max(maxLength);
  return z.union([
    value(minLength),
    z
      .record(LocaleCode, value(Math.max(1, minLength)))
      .refine((m) => Object.keys(m).length >= 1, { message: 'Add at least one locale' })
      .refine((m) => Object.keys(m).length <= LOCALIZED_TEXT_LIMITS.maxLocales, {
        message: `At most ${LOCALIZED_TEXT_LIMITS.maxLocales} locales`,
      }),
  ]);
}

/** LocalizedText with the default limits. */
export const LocalizedText = localizedText();

/** Whether a value is the map form (`{ en: '…', de: '…' }`). */
export function isLocalizedTextMap(text: LocalizedText | null | undefined): text is LocalizedTextMap {
  return typeof text === 'object' && text !== null && !Array.isArray(text);
}

/**
 * A localized text read from storage (a JSON column, a stored payload) without trusting its shape: a
 * string stays a string, an object keeps its string values, anything else is `null`. What
 * `resolveText` and `localesOf` then read is always a LocalizedText.
 */
export function localizedTextOf(value: unknown): LocalizedText | null {
  if (typeof value === 'string') return value;
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return null;
  const map: LocalizedTextMap = {};
  for (const [locale, text] of Object.entries(value)) if (typeof text === 'string') map[locale] = text;
  return map;
}

/** The language part of a locale code: `de-AT` → `de`. */
export function languageOf(locale: string): string {
  return locale.split('-')[0]!.toLowerCase();
}

/** First non-empty value of a key list, in order. */
function pick(map: LocalizedTextMap, keys: Iterable<string>): string | undefined {
  for (const k of keys) {
    const v = map[k];
    if (typeof v === 'string' && v.length > 0) return v;
  }
  return undefined;
}

/**
 * Text of a LocalizedText for a locale: the exact locale, then its language, then `fallback`
 * (and its language), then the first non-empty value. A plain string is returned for every
 * locale. `null`/`undefined` resolve to `''`.
 *
 *   resolveText({ en: 'Deals', de: 'Verkaufschancen' }, 'de')    // 'Verkaufschancen'
 *   resolveText({ en: 'Deals', de: 'Verkaufschancen' }, 'de-AT') // 'Verkaufschancen'
 *   resolveText({ en: 'Deals' }, 'fr')                           // 'Deals'
 *   resolveText({ de: 'Geräte' }, 'fr')                          // 'Geräte'
 */
export function resolveText(text: LocalizedText | null | undefined, locale: string, fallback = 'en'): string {
  if (text == null) return '';
  if (typeof text === 'string') return text;
  const keys = [locale, languageOf(locale), fallback, languageOf(fallback)];
  return pick(text, keys) ?? pick(text, Object.keys(text)) ?? '';
}

/** Locales that have a non-empty value (empty for a plain string, which covers all locales). */
export function localesOf(text: LocalizedText | null | undefined): string[] {
  if (!isLocalizedTextMap(text)) return [];
  return Object.keys(text).filter((k) => (text[k] ?? '').length > 0);
}

/**
 * Set the value of one locale. Setting a plain string's locale turns it into a map that keeps the
 * old text under `baseLocale` (default `en`); an empty value removes the locale; a map left with a
 * single `baseLocale` value collapses back to a plain string.
 */
export function withLocale(
  text: LocalizedText | null | undefined,
  locale: string,
  value: string,
  baseLocale = 'en',
): LocalizedText {
  const map: LocalizedTextMap = isLocalizedTextMap(text) ? { ...text } : text ? { [baseLocale]: text } : {};
  if (value.length > 0) map[locale] = value;
  else delete map[locale];
  const keys = Object.keys(map);
  if (keys.length === 0) return '';
  if (keys.length === 1 && keys[0] === baseLocale) return map[baseLocale]!;
  return map;
}

/** Apply `fn` to every value (e.g. trimming) and keep the form. */
export function mapLocalizedText(text: LocalizedText, fn: (value: string, locale: string | null) => string) {
  if (typeof text === 'string') return fn(text, null);
  return Object.fromEntries(Object.entries(text).map(([k, v]) => [k, fn(v, k)])) as LocalizedTextMap;
}
